import { useCallback, useMemo, useState } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslations } from '@/i18n/use-translations';

export type DemandTimelineBucket = {
  at: string;
  required: number;
  capacity?: number;
  covered?: number;
  shortfall?: number;
  spareCapacity?: number;
};

type OpsDemandTimelineProps = {
  buckets: DemandTimelineBucket[];
  /** Fallback capacity when a bucket omits `capacity` (legacy flat connected count). */
  connectedCapacity: number;
  peakAt?: string | null;
  peakParallelRequired48h?: number;
  height?: number;
};

const VIEW_WIDTH = 240;
const VIEW_HEIGHT = 44;
const BAR_GAP = 0.35;
const HORIZON_HOURS = 48;
const HOUR_MS = 60 * 60 * 1000;

function floorToHourUtc(date: Date): Date {
  const aligned = new Date(date);
  aligned.setUTCMinutes(0, 0, 0);
  return aligned;
}

function resolveBucketCapacity(bucket: DemandTimelineBucket, fallbackCapacity: number): number {
  return typeof bucket.capacity === 'number' ? bucket.capacity : fallbackCapacity;
}

function splitDemandByCapacity(required: number, capacity: number) {
  if (required <= 0) {
    return { covered: 0, shortfall: 0 };
  }
  return {
    covered: Math.min(required, capacity),
    shortfall: Math.max(0, required - capacity),
  };
}

/** Prefer the server hourly series; otherwise merge sparse buckets into 48 UTC hour slots. */
function normalizeHourlyBuckets(buckets: DemandTimelineBucket[]): DemandTimelineBucket[] {
  if (buckets.length >= HORIZON_HOURS) {
    return buckets.slice(0, HORIZON_HOURS);
  }

  const start = floorToHourUtc(new Date());
  const slots: DemandTimelineBucket[] = Array.from({ length: HORIZON_HOURS }, (_, index) => ({
    at: new Date(start.getTime() + index * HOUR_MS).toISOString(),
    required: 0,
  }));
  const indexByHour = new Map(slots.map((slot, index) => [floorToHourUtc(new Date(slot.at)).getTime(), index]));

  for (const bucket of buckets) {
    const hourIndex = indexByHour.get(floorToHourUtc(new Date(bucket.at)).getTime());
    if (hourIndex == null) continue;
    slots[hourIndex] = {
      ...slots[hourIndex],
      required: Math.max(slots[hourIndex].required, bucket.required),
      capacity: bucket.capacity ?? slots[hourIndex].capacity,
      covered: bucket.covered ?? slots[hourIndex].covered,
      shortfall: bucket.shortfall ?? slots[hourIndex].shortfall,
      spareCapacity: bucket.spareCapacity ?? slots[hourIndex].spareCapacity,
    };
  }

  return slots;
}

type BarStack = {
  required: number;
  covered: number;
  shortfall: number;
  capacity: number;
};

function buildBarStack(bucket: DemandTimelineBucket, fallbackCapacity: number): BarStack {
  const capacity = resolveBucketCapacity(bucket, fallbackCapacity);
  const required = bucket.required;
  if (required === 0) {
    return { required: 0, covered: 0, shortfall: 0, capacity };
  }

  if (typeof bucket.covered === 'number' && typeof bucket.shortfall === 'number') {
    return { required, covered: bucket.covered, shortfall: bucket.shortfall, capacity };
  }

  const { covered, shortfall } = splitDemandByCapacity(required, capacity);
  return { required, covered, shortfall, capacity };
}

/** 48h hourly bars: demand vs capacity per hour (capacity may vary with maintenance windows). */
export default function OpsDemandTimeline({
  buckets,
  connectedCapacity,
  peakAt,
  peakParallelRequired48h = 0,
  height = 52,
}: OpsDemandTimelineProps) {
  const t = useTranslations();
  const theme = useTheme();
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  const hourlyBuckets = useMemo(() => normalizeHourlyBuckets(buckets), [buckets]);
  const hasDemand = hourlyBuckets.some((bucket) => bucket.required > 0) || peakParallelRequired48h > 0;

  const formatTooltip = useCallback(
    (at: string, stack: BarStack) => {
      const time = new Date(at).toLocaleString(undefined, {
        weekday: 'short',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'UTC',
      });
      const parts = [
        `${time} UTC`,
        t('admin.opsAdmin.demandTimeline.tooltipRequired', { count: stack.required }, `${stack.required} required`),
        t('admin.opsAdmin.demandTimeline.tooltipCapacity', { count: stack.capacity }, `${stack.capacity} capacity`),
      ];
      if (stack.covered > 0) {
        parts.push(
          t('admin.opsAdmin.demandTimeline.tooltipCovered', { count: stack.covered }, `${stack.covered} covered`),
        );
      }
      if (stack.shortfall > 0) {
        parts.push(
          t('admin.opsAdmin.demandTimeline.tooltipShort', { count: stack.shortfall }, `${stack.shortfall} short`),
        );
      } else if (stack.capacity > stack.required) {
        parts.push(
          t(
            'admin.opsAdmin.demandTimeline.tooltipSpare',
            { count: stack.capacity - stack.required },
            `${stack.capacity - stack.required} spare`,
          ),
        );
      }
      return parts.join(' · ');
    },
    [t],
  );

  const chart = useMemo(() => {
    const stacks = hourlyBuckets.map((bucket) => buildBarStack(bucket, connectedCapacity));
    const peakRequired = Math.max(
      stacks.reduce((max, stack) => Math.max(max, stack.required, stack.capacity), 0),
      peakParallelRequired48h,
      connectedCapacity,
      1,
    );
    const chartHeight = VIEW_HEIGHT;
    const barSlot = VIEW_WIDTH / HORIZON_HOURS;
    const barWidth = Math.max(barSlot - BAR_GAP, 0.5);

    const bars = stacks.map((stack, index) => {
      const coveredHeight = (stack.covered / peakRequired) * chartHeight;
      const shortfallHeight = (stack.shortfall / peakRequired) * chartHeight;
      const totalHeight = coveredHeight + shortfallHeight;
      const x = index * barSlot + BAR_GAP / 2;
      const yTop = chartHeight - totalHeight;
      const yShortfall = chartHeight - coveredHeight - shortfallHeight;
      const yCovered = chartHeight - coveredHeight;
      const capacityY = chartHeight - (stack.capacity / peakRequired) * chartHeight;

      return {
        index,
        bucket: hourlyBuckets[index],
        stack,
        x,
        barWidth,
        yTop,
        coveredHeight,
        shortfallHeight,
        yShortfall,
        yCovered,
        capacityY,
      };
    });

    const capacityPath = bars
      .map((bar, index) => {
        const x0 = bar.x;
        const x1 = bar.x + bar.barWidth;
        const y = bar.capacityY;
        return index === 0 ? `M ${x0} ${y} L ${x1} ${y}` : `L ${x0} ${y} L ${x1} ${y}`;
      })
      .join(' ');

    const maxCapacity = stacks.reduce((max, stack) => Math.max(max, stack.capacity), 0);

    return { peakRequired, bars, chartHeight, capacityPath, maxCapacity };
  }, [hourlyBuckets, connectedCapacity, peakParallelRequired48h]);

  if (!hasDemand) {
    return (
      <Typography variant="caption" color="text.secondary">
        {t('admin.opsAdmin.demandTimeline.empty', undefined, 'No demand in horizon')}
      </Typography>
    );
  }

  const hoverBar = hoverIndex != null ? chart.bars[hoverIndex] : null;
  const peakHour = peakAt ? floorToHourUtc(new Date(peakAt)).toISOString() : null;

  return (
    <Box>
      <Box
        component="svg"
        viewBox={`0 0 ${VIEW_WIDTH} ${VIEW_HEIGHT}`}
        preserveAspectRatio="none"
        sx={{ width: '100%', height: height - 20, display: 'block' }}
        onMouseLeave={() => setHoverIndex(null)}
      >
        {chart.maxCapacity > 0 && (
          <path
            d={chart.capacityPath}
            fill="none"
            stroke={theme.palette.text.secondary}
            strokeWidth={0.4}
            strokeDasharray="2 2"
            opacity={0.55}
          />
        )}
        {chart.bars.map((bar) => {
          const active = hoverIndex === bar.index;
          const isPeakHour = peakHour != null && floorToHourUtc(new Date(bar.bucket.at)).toISOString() === peakHour;
          return (
            <g key={bar.index} onMouseEnter={() => setHoverIndex(bar.index)}>
              {bar.coveredHeight > 0 && (
                <rect
                  x={bar.x}
                  y={bar.yCovered}
                  width={bar.barWidth}
                  height={bar.coveredHeight}
                  fill={theme.palette.info.main}
                  opacity={active ? 0.7 : 0.45}
                />
              )}
              {bar.shortfallHeight > 0 && (
                <rect
                  x={bar.x}
                  y={bar.yShortfall}
                  width={bar.barWidth}
                  height={bar.shortfallHeight}
                  fill={theme.palette.error.main}
                  opacity={active ? 1 : 0.9}
                />
              )}
              {bar.stack.required === 0 && (
                <rect
                  x={bar.x}
                  y={chart.chartHeight - 1}
                  width={bar.barWidth}
                  height={1}
                  fill={theme.palette.divider}
                />
              )}
              {isPeakHour && bar.stack.required > 0 && (
                <polygon
                  points={`${bar.x + bar.barWidth / 2},${bar.yTop - 1.5} ${bar.x + bar.barWidth / 2 - 1.5},${bar.yTop + 0.5} ${bar.x + bar.barWidth / 2 + 1.5},${bar.yTop + 0.5}`}
                  fill={theme.palette.text.primary}
                />
              )}
            </g>
          );
        })}
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.15 }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.58rem' }}>
          {t('admin.opsAdmin.demandTimeline.now', undefined, 'now')}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.58rem' }}>
          {t('admin.opsAdmin.demandTimeline.plus24h', undefined, '+24h')}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.58rem' }}>
          {t('admin.opsAdmin.demandTimeline.plus48h', undefined, '+48h')}
        </Typography>
      </Box>
      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" sx={{ mt: 0.25, rowGap: 0.25 }}>
        <LegendSwatch
          color={theme.palette.info.main}
          opacity={0.55}
          label={t('admin.opsAdmin.demandTimeline.legendCovered', undefined, 'Covered')}
        />
        <LegendSwatch
          color={theme.palette.error.main}
          opacity={0.9}
          label={t('admin.opsAdmin.demandTimeline.legendShort', undefined, 'Short')}
        />
        <LegendLine
          label={t('admin.opsAdmin.demandTimeline.legendCapacity', undefined, 'Capacity')}
          color={theme.palette.text.secondary}
        />
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.58rem', ml: 'auto !important' }}>
          {t('admin.opsAdmin.demandTimeline.hourlyCapacity', undefined, 'hourly capacity')}
        </Typography>
      </Stack>
      {hoverBar && (
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.62rem', display: 'block', mt: 0.2 }}>
          {formatTooltip(hoverBar.bucket.at, hoverBar.stack)}
        </Typography>
      )}
    </Box>
  );
}

function LegendLine({ color, label }: { color: string; label: string }) {
  return (
    <Stack direction="row" spacing={0.35} alignItems="center">
      <Box
        sx={{
          width: 10,
          height: 0,
          borderTop: `1.5px dashed ${color}`,
          opacity: 0.7,
        }}
      />
      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.58rem' }}>
        {label}
      </Typography>
    </Stack>
  );
}

function LegendSwatch({ color, opacity, label }: { color: string; opacity: number; label: string }) {
  return (
    <Stack direction="row" spacing={0.35} alignItems="center">
      <Box sx={{ width: 8, height: 6, bgcolor: color, opacity, borderRadius: 0.25 }} />
      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.58rem' }}>
        {label}
      </Typography>
    </Stack>
  );
}
